import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import { useTranslation } from 'src/arb-wrapper';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import {
  useBusinessUnitAdminControllerFindGroupsQuery,
  useBusinessUnitAdminControllerFindMembersQuery,
  useBusinessUnitAdminControllerFindPartnersQuery,
} from 'src/store/leela-api';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { GroupRoleLabelKeys } from 'src/utils/groupRoles';
import type { GroupRole } from 'src/utils/groupRoles';
import { GroupStatusLabelKeys, STATUS_TAG_TYPE } from 'src/utils/groupStatus';
import type { GroupStatus } from 'src/utils/groupStatus';
import { CreateGroupModal } from './CreateGroupModal';
import { EditGroupModal } from './EditGroupModal';

interface GroupsTabProps {
  businessUnitId: string;
  businessUnitCountries: string[];
}

interface GroupRow {
  id: string;
  name: string;
  role: GroupRole;
  countries: string[];
  publishers: string[];
  businessLines: string[];
  status: GroupStatus;
  partnerId?: string;
  dataVisibilityPolicy?: string;
}

export const GroupsTab: React.FC<GroupsTabProps> = ({
  businessUnitId,
  businessUnitCountries,
}) => {
  const { t } = useTranslation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupRow | undefined>();
  const { data, isError, isLoading } =
    useBusinessUnitAdminControllerFindGroupsQuery({
      businessUnitId,
      page: 1,
      pageSize: PERMISSION_LIST_PAGE_SIZE,
    });
  const { data: partnersData } =
    useBusinessUnitAdminControllerFindPartnersQuery({
      id: businessUnitId,
      page: 1,
      pageSize: PERMISSION_LIST_PAGE_SIZE,
    });

  const partnerNameMap = new Map(
    ((partnersData?.data ?? []) as Array<{ id: string; name: string }>).map(
      (partner) => [partner.id, partner.name],
    ),
  );

  if (isLoading) return <Loader />;
  if (isError) { return <Alert type="error">{t('access_management_load_error')}</Alert>; }

  const groups = (data?.data ?? []) as GroupRow[];

  return (
    <Column data-testid="access-groups-tab" spacing="300">
      <Row alignmentHorizontal="end">
        <Button
          data-testid="access-groups-create-button"
          type="primary"
          onClick={() => {
            setIsCreateOpen(true);
          }}
        >
          <Icon tokens={plusTokens} />
          {t('access_management_create_group')}
        </Button>
      </Row>

      <Text
        color="secondary"
        data-testid="access-groups-name-format"
        type="b200"
      >
        {/* Empty params force ICU formatting so escaped braces render literally. */}
        {t('access_management_group_name_format', {})}
      </Text>

      {groups.length === 0 && (
        <Text color="secondary" data-testid="access-groups-empty" type="b200">
          {t('bu_management_no_groups')}
        </Text>
      )}

      {groups.map((group) => (
        <GroupExpandableRow
          group={group}
          key={group.id}
          partnerNameMap={partnerNameMap}
          onEdit={() => {
            setEditingGroup(group);
          }}
        />
      ))}

      <CreateGroupModal
        businessUnitCountries={businessUnitCountries}
        businessUnitId={businessUnitId}
        open={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
        }}
      />

      {editingGroup && (
        <EditGroupModal
          businessUnitCountries={businessUnitCountries}
          group={editingGroup}
          open={!!editingGroup}
          partnerName={
            editingGroup.partnerId
              ? partnerNameMap.get(editingGroup.partnerId)
              : undefined
          }
          onClose={() => {
            setEditingGroup(undefined);
          }}
        />
      )}
    </Column>
  );
};

interface GroupExpandableRowProps {
  group: GroupRow;
  onEdit: () => void;
  partnerNameMap: Map<string, string>;
}

const GroupExpandableRow: React.FC<GroupExpandableRowProps> = ({
  group,
  onEdit,
  partnerNameMap,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);

  const { data: membersData, isLoading: isMembersLoading } =
    useBusinessUnitAdminControllerFindMembersQuery(
      { id: group.id, page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
      { skip: !hasExpanded },
    );

  const members = (membersData?.data ?? []) as Array<{
    id: string;
    userId: string;
    expiresAt?: string;
  }>;

  const handleToggle = () => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      data-testid={`access-group-row-${group.id}`}
      spacingInset="400"
      type="outline"
    >
      <Column spacing="300">
        <div
          aria-expanded={isExpanded}
          data-testid={`access-group-toggle-${group.id}`}
          role="button"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (
              event.currentTarget === event.target &&
              (event.key === 'Enter' || event.key === ' ')
            ) {
              event.preventDefault();
              handleToggle();
            }
          }}
        >
          <div
            data-testid={`access-group-name-container-${group.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Icon
              tokens={
                isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens
              }
            />
            <ShrinkableTextValue testId={`access-group-name-${group.id}`}>
              {group.name}
            </ShrinkableTextValue>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag type={STATUS_TAG_TYPE[group.status]}>
              {t(GroupStatusLabelKeys[group.status])}
            </Tag>
            <Button
              aria-label={t('access_management_edit_group')}
              data-testid={`access-group-edit-${group.id}`}
              propagateClickEvent={false}
              type="icon"
              onClick={onEdit}
            >
              <Icon tokens={editTokens} />
            </Button>
          </div>
        </div>

        {isExpanded && (
          <Box backgroundColor="subdued" spacingInset="400">
            <Column spacing="200">
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_role_label')}
                </Text>
                <Text>
                  {GroupRoleLabelKeys[group.role]
                    ? t(GroupRoleLabelKeys[group.role]) ?? group.role
                    : group.role}
                </Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_countries_label')}
                </Text>
                <Text>
                  {group.countries.length > 0
                    ? group.countries.join(', ')
                    : t('access_management_all')}
                </Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_publishers_label')}
                </Text>
                <Text>
                  {group.publishers.length > 0
                    ? group.publishers.join(', ')
                    : t('access_management_all')}
                </Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_business_lines_label')}
                </Text>
                <Text>
                  {group.businessLines.length > 0
                    ? group.businessLines.join(', ')
                    : t('access_management_all')}
                </Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_partner_label')}
                </Text>
                <Text>
                  {group.partnerId
                    ? partnerNameMap.get(group.partnerId) ?? group.partnerId
                    : t('access_management_internal')}
                </Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">
                  {t('access_management_data_policy_label')}
                </Text>
                <Text>
                  {group.dataVisibilityPolicy ??
                    t('access_management_not_available')}
                </Text>
              </Row>

              <Divider />
              <Text>{t('access_management_members_label')}</Text>
              {isMembersLoading && <Loader />}
              {!isMembersLoading && members.length === 0 && (
                <Text color="secondary" type="b200">
                  {t('access_management_no_group_members')}
                </Text>
              )}
              {members.map((member) => (
                <Row key={member.id} spacing="200">
                  <Text>{member.userId}</Text>
                  {member.expiresAt && (
                    <Text color="secondary" type="b200">
                      {t('access_management_member_expires', {
                        date: formatUtcDate(
                          member.expiresAt,
                          t('access_management_not_available'),
                        ),
                      })}
                    </Text>
                  )}
                </Row>
              ))}
            </Column>
          </Box>
        )}
      </Column>
    </Box>
  );
};
